import { Component, EventEmitter, Input, OnInit, OnChanges, Output, SimpleChanges, DestroyRef, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { UnespCoreMessageService } from 'src/libs/unesp-core'
import { AnotacaoProvaService, AnotacaoProvaDTO, AnotacaoProvaPayload } from 'src/app/services/anotacao-prova.service'

@Component({
  selector: 'app-anotacao-sidebar',
  templateUrl: './anotacao-sidebar.component.html',
  styleUrls: ['./anotacao-sidebar.component.scss'],
})
export class AnotacaoSidebarComponent implements OnInit, OnChanges {
  private destroyRef = inject(DestroyRef)

  @Input() hash: string | null = null
  @Input() inscricaoId: number | null = null
  @Input() documentoId!: number
  @Input() tipoProva!: string

  @Output() fechar = new EventEmitter<void>()

  anotacoes: AnotacaoProvaDTO[] = []
  novoTexto = ''
  novaPagina: number | null = null
  carregando = false
  enviando = false

  editandoId: number | null = null
  editandoTexto = ''
  editandoPagina: number | null = null
  excluindoId: number | null = null

  constructor(
    private anotacaoProvaService: AnotacaoProvaService,
    private unespCoreMessageService: UnespCoreMessageService,
  ) {}

  ngOnInit(): void {
    this.carregarAnotacoes()
  }

  ngOnChanges(changes: SimpleChanges): void {
    const relevantChange = changes['documentoId'] || changes['hash'] || changes['inscricaoId']
    if (relevantChange && !relevantChange.firstChange) {
      this.carregarAnotacoes()
    }
  }

  carregarAnotacoes(): void {
    if (!this.documentoId || (!this.hash && !this.inscricaoId)) return

    this.carregando = true

    const obs = this.hash
      ? this.anotacaoProvaService.listarPorHash(this.hash, this.documentoId)
      : this.anotacaoProvaService.listar(this.documentoId)

    obs.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (anotacoes) => {
        this.anotacoes = anotacoes
        this.carregando = false
      },
      error: () => {
        this.carregando = false
      },
    })
  }

  enviar(): void {
    if (!this.novoTexto.trim() || (!this.hash && !this.inscricaoId)) return

    this.enviando = true
    const payload: AnotacaoProvaPayload = {
      texto: this.novoTexto.trim(),
      pagina: this.novaPagina,
      tipoProva: this.tipoProva,
    }

    const obs = this.hash
      ? this.anotacaoProvaService.criarPorHash(this.hash, this.documentoId, payload)
      : this.anotacaoProvaService.criar(this.documentoId, payload)

    obs.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (anotacao) => {
        this.anotacoes.push(anotacao)
        this.novoTexto = ''
        this.novaPagina = null
        this.enviando = false
      },
      error: () => {
        this.enviando = false
      },
    })
  }

  iniciarEdicao(anotacao: AnotacaoProvaDTO): void {
    this.editandoId = anotacao.id
    this.editandoTexto = anotacao.texto
    this.editandoPagina = anotacao.pagina
  }

  cancelarEdicao(): void {
    this.editandoId = null
    this.editandoTexto = ''
    this.editandoPagina = null
  }

  salvarEdicao(anotacao: AnotacaoProvaDTO): void {
    if (!this.editandoTexto.trim()) return

    const payload: AnotacaoProvaPayload = {
      texto: this.editandoTexto.trim(),
      pagina: this.editandoPagina,
      tipoProva: this.tipoProva,
    }

    const obs = this.hash
      ? this.anotacaoProvaService.editarPorHash(this.hash, this.documentoId, anotacao.id, payload)
      : this.anotacaoProvaService.editar(this.documentoId, anotacao.id, payload)

    obs.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (atualizada) => {
        const idx = this.anotacoes.findIndex(a => a.id === anotacao.id)
        if (idx >= 0) this.anotacoes[idx] = atualizada
        this.cancelarEdicao()
      },
      error: () => {
        this.unespCoreMessageService.showMessageError('Erro ao salvar anotação.')
        this.cancelarEdicao()
      },
    })
  }

  confirmarExclusao(anotacao: AnotacaoProvaDTO): void {
    this.excluindoId = anotacao.id
  }

  cancelarExclusao(): void {
    this.excluindoId = null
  }

  executarExclusao(anotacao: AnotacaoProvaDTO): void {
    const obs = this.hash
      ? this.anotacaoProvaService.excluirPorHash(this.hash, this.documentoId, anotacao.id)
      : this.anotacaoProvaService.excluir(this.documentoId, anotacao.id, this.tipoProva)

    obs.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.anotacoes = this.anotacoes.filter(a => a.id !== anotacao.id)
        this.excluindoId = null
      },
      error: () => {
        this.unespCoreMessageService.showMessageError('Erro ao excluir anotação.')
        this.excluindoId = null
      },
    })
  }

  bloquearCaracteresInvalidos(event: KeyboardEvent): boolean {
    if (['-', 'e', 'E', '.', ','].includes(event.key)) {
      event.preventDefault()
      return false
    }
    return true
  }

  formatarTempo(dataStr: string): string {
    const data = new Date(dataStr)
    const agora = new Date()
    const diffMs = agora.getTime() - data.getTime()
    const diffMin = Math.floor(diffMs / 60000)

    if (diffMin < 1) return 'agora'
    if (diffMin < 60) return `há ${diffMin} min`
    const diffHoras = Math.floor(diffMin / 60)
    if (diffHoras < 24) return `há ${diffHoras}h`
    const diffDias = Math.floor(diffHoras / 24)
    if (diffDias < 7) return `há ${diffDias}d`
    return data.toLocaleDateString('pt-BR')
  }
}
